import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { LogIn, LogOut, Mail, Plus, Sprout, Trash2, Users } from "lucide-react";
import Seo from "@/components/Seo";
import { ASSETS } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleAuthRedirect() {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthCallback({ onDone }) {
  const hasProcessed = useRef(false);
  if (!hasProcessed.current) {
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    if (sessionId) {
      axios
        .post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then(() => {
          window.history.replaceState(null, "", window.location.pathname);
          onDone();
        })
        .catch(() => {
          window.history.replaceState(null, "", window.location.pathname);
          onDone();
        });
    }
  }
  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <p className="font-display text-2xl italic text-forest">Setting the table…</p>
    </div>
  );
}

const PLAN_ITEMS = [
  { name: "3 Day Plan", description: "6 meals · Lunch & Dinner", price: "120", meal_count: "6" },
  { name: "4 Day Plan", description: "8 meals · Lunch & Dinner", price: "150", meal_count: "8" },
  { name: "5 Day Plan", description: "10 meals · Lunch & Dinner", price: "175", meal_count: "10" },
];
const EMPTY_MEAL = { name: "", description: "" };
const EMPTY_MENU = {
  week_label: "",
  meals: [{ ...EMPTY_MEAL }, { ...EMPTY_MEAL }, { ...EMPTY_MEAL }],
  notes: "Vegetarian and Vegan substitutions are available. Delivery included on every plan, per week. 4 & 5 Day Plans get all 3 options.",
  limit_per_item: "10",
  alacarte_enabled: false,
  alacarte_price: "15",
  alacarte_fee: "8",
  items: PLAN_ITEMS.map((p) => ({ ...p })),
};

function Dashboard({ user }) {
  const [inquiries, setInquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [menus, setMenus] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tw, setTw] = useState({ quote: "", guest: "", context: "" });
  const [menuForm, setMenuForm] = useState(EMPTY_MENU);
  const [menuMsg, setMenuMsg] = useState("");
  const [wordMsg, setWordMsg] = useState("");

  useEffect(() => {
    const get = (url, fn) => axios.get(`${API}${url}`, { withCredentials: true }).then(({ data }) => fn(data)).catch(() => {});
    get("/admin/inquiries", setInquiries);
    get("/admin/subscribers", setSubscribers);
    get("/testimonials", setTestimonials);
    get("/admin/menus", setMenus);
    get("/admin/orders", setOrders);
  }, []);

  const signOut = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    window.location.reload();
  };

  const addWord = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/testimonials`, tw, { withCredentials: true });
      setTw({ quote: "", guest: "", context: "" });
      const { data } = await axios.get(`${API}/testimonials`);
      setTestimonials(data);
      setWordMsg("Added — it's live on the site.");
    } catch (err) {
      setWordMsg(err?.response?.data?.detail || "Could not save. Try again.");
    }
  };

  const removeWord = async (id) => {
    await axios.delete(`${API}/admin/testimonials/${id}`, { withCredentials: true });
    setTestimonials((t) => t.filter((x) => x.id !== id));
  };

  const saveMenu = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/menus`,
        {
          week_label: menuForm.week_label,
          meals: menuForm.meals.filter((m) => m.name.trim()),
          notes: menuForm.notes,
          limit_per_item: Number(menuForm.limit_per_item) || 10,
          alacarte: menuForm.alacarte_enabled
            ? { price: Number(menuForm.alacarte_price) || 15, delivery_fee: Number(menuForm.alacarte_fee) || 0 }
            : null,
          items: menuForm.items.map((it) => ({
            ...it,
            price: Number(it.price),
            meal_count: it.meal_count ? Number(it.meal_count) : null,
          })),
        },
        { withCredentials: true }
      );
      setMenuForm(EMPTY_MENU);
      const { data } = await axios.get(`${API}/admin/menus`, { withCredentials: true });
      setMenus(data);
      setMenuMsg("Published — it's live on the meal prep page (previous menu archived).");
    } catch (err) {
      setMenuMsg(err?.response?.data?.detail || "Could not publish. Check the fields and try again.");
    }
  };

  const archiveMenu = async (id) => {
    await axios.post(`${API}/admin/menus/${id}/archive`, {}, { withCredentials: true });
    const { data } = await axios.get(`${API}/admin/menus`, { withCredentials: true });
    setMenus(data);
  };

  const setMenuVisibility = async (id, show) => {
    try {
      await axios.post(`${API}/admin/menus/${id}/visibility`, { show }, { withCredentials: true });
      setMenus((ms) => ms.map((m) => (m.id === id ? { ...m, show_in_archive: show } : m)));
    } catch {
      window.alert("Could not update this menu — please sign in again and try once more.");
    }
  };

  const deleteMenu = async (id) => {
    try {
      await axios.delete(`${API}/admin/menus/${id}`, { withCredentials: true });
      setMenus((ms) => ms.filter((m) => m.id !== id));
    } catch {
      window.alert("Could not delete this menu — please sign in again and try once more.");
    }
  };

  const setItem = (i, k, v) =>
    setMenuForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));

  return (
    <div data-testid="owner-dashboard" className="mx-auto max-w-7xl px-6 pb-24 pt-14 md:px-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.24em] text-terracotta">Owner dashboard</p>
          <h1 className="mt-3 font-display text-4xl font-light text-forest">Welcome back, {user.name?.split(" ")[0]}</h1>
        </div>
        <button
          onClick={signOut}
          data-testid="signout-button"
          className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-forest px-5 py-2.5 text-sm font-medium text-forest transition-colors hover:bg-forest hover:text-cream"
        >
          <LogOut size={15} /> Sign out
        </button>
      </div>

      {/* ------- Meal prep menu builder ------- */}
      <div className="mt-14 rounded-3xl border border-forest/15 bg-white/50 p-6 md:p-10" data-testid="admin-menu-builder">
        <h2 className="font-display text-3xl font-light text-forest">This week's meal prep menu</h2>
        <p className="mt-2 text-sm text-ink/60">
          Publish a new menu and the previous one moves to the public archive automatically. Past menus can be hidden from the site or deleted below.
        </p>
        <form onSubmit={saveMenu} className="mt-6">
          <input
            required
            value={menuForm.week_label}
            onChange={(e) => setMenuForm((f) => ({ ...f, week_label: e.target.value }))}
            placeholder='Week label — e.g. "Week of November 3"'
            data-testid="menu-week-label-input"
            className="field max-w-md"
          />

          <p className="mt-8 text-xs font-medium uppercase tracking-[0.18em] text-terracotta">This week's meals</p>
          <div className="mt-4 space-y-4">
            {menuForm.meals.map((m, i) => (
              <div key={i} className="grid gap-3 md:grid-cols-12" data-testid={`menu-meal-row-${i + 1}`}>
                <input
                  value={m.name}
                  onChange={(e) =>
                    setMenuForm((f) => ({ ...f, meals: f.meals.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) }))
                  }
                  placeholder={`Meal ${["one", "two", "three"][i] || i + 1} — e.g. { Sunday Roast with a Yard Twist }`}
                  data-testid={`menu-meal-name-${i + 1}`}
                  className="field md:col-span-4"
                />
                <input
                  value={m.description}
                  onChange={(e) =>
                    setMenuForm((f) => ({ ...f, meals: f.meals.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) }))
                  }
                  placeholder="Description"
                  data-testid={`menu-meal-desc-${i + 1}`}
                  className="field md:col-span-8"
                />
              </div>
            ))}
          </div>

          <div className="mt-6">
            <label className="field-label" htmlFor="menu-notes">Menu notes (substitutions, delivery, etc.)</label>
            <input
              id="menu-notes"
              value={menuForm.notes}
              onChange={(e) => setMenuForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="menu-notes-input"
              className="field"
            />
          </div>

          <div className="mt-6 max-w-xs">
            <label className="field-label" htmlFor="menu-limit">Order limit per plan</label>
            <input
              id="menu-limit"
              type="number"
              min="1"
              max="100"
              value={menuForm.limit_per_item}
              onChange={(e) => setMenuForm((f) => ({ ...f, limit_per_item: e.target.value }))}
              data-testid="menu-limit-input"
              className="field"
            />
          </div>

          <div className="mt-8 rounded-2xl border border-gold/40 bg-gold/5 p-5" data-testid="alacarte-builder">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={menuForm.alacarte_enabled}
                onChange={(e) => setMenuForm((f) => ({ ...f, alacarte_enabled: e.target.checked }))}
                data-testid="alacarte-enable"
                className="h-4 w-4"
              />
              <span className="text-sm font-medium text-forest">Offer à la carte this week — $15 per meal + delivery fee</span>
            </label>
            {menuForm.alacarte_enabled && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="alacarte-price">Price per meal ($)</label>
                  <input
                    id="alacarte-price"
                    type="number"
                    min="1"
                    step="0.01"
                    value={menuForm.alacarte_price}
                    onChange={(e) => setMenuForm((f) => ({ ...f, alacarte_price: e.target.value }))}
                    data-testid="alacarte-price-input"
                    className="field"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="alacarte-fee">Delivery fee ($ per order)</label>
                  <input
                    id="alacarte-fee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={menuForm.alacarte_fee}
                    onChange={(e) => setMenuForm((f) => ({ ...f, alacarte_fee: e.target.value }))}
                    data-testid="alacarte-fee-input"
                    className="field"
                  />
                </div>
              </div>
            )}
          </div>

          <p className="mt-8 text-xs font-medium uppercase tracking-[0.18em] text-terracotta">Plans</p>
          <div className="mt-4 space-y-4">
            {menuForm.items.map((it, i) => (
              <div key={i} className="grid gap-3 md:grid-cols-12" data-testid={`menu-item-row-${i + 1}`}>
                <input required value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Dish name" data-testid={`menu-item-name-${i + 1}`} className="field md:col-span-3" />
                <input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Short description" data-testid={`menu-item-desc-${i + 1}`} className="field md:col-span-5" />
                <input required type="number" step="0.01" min="1" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} placeholder="Price $" data-testid={`menu-item-price-${i + 1}`} className="field md:col-span-2" />
                <input required type="number" min="1" max="50" value={it.meal_count} onChange={(e) => setItem(i, "meal_count", e.target.value)} placeholder="Meals" title="Meals included in this plan" data-testid={`menu-item-meals-${i + 1}`} className="field md:col-span-2" />
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => setMenuForm((f) => ({ ...f, items: [...f.items, { name: "", description: "", price: "", meal_count: "" }] }))}
              data-testid="add-menu-item-button"
              className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-forest px-5 py-2.5 text-sm font-medium text-forest transition-colors hover:bg-forest hover:text-cream"
            >
              <Plus size={15} /> Add dish
            </button>
            <button
              type="submit"
              data-testid="publish-menu-button"
              className="inline-flex cursor-pointer items-center rounded-full bg-terracotta px-7 py-3 text-sm font-medium text-cream transition-colors hover:bg-gold hover:text-deepforest"
            >
              Publish menu
            </button>
            {menuMsg && <p data-testid="menu-publish-status" className="text-sm text-terracotta">{menuMsg}</p>}
          </div>
        </form>

        {menus.length > 0 && (
          <div className="mt-8 space-y-3">
            {menus.map((m) => (
              <div key={m.id} data-testid={`admin-menu-row-${m.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-forest/10 px-5 py-4">
                <div>
                  <p className="font-display text-lg text-forest">{m.week_label}</p>
                  <p className="mt-1 text-sm text-ink/60" data-testid={`menu-row-claims-${m.id}`}>
                    {m.items.map((it) => `${it.name}: ${it.sold || 0}/${m.limit_per_item || 10} claimed`).join(" · ")}
                  </p>
                </div>
                {m.status === "active" ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full bg-gold/25 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-forest">Live now</span>
                    <button
                      onClick={() => {
                        if (window.confirm("Take down this menu? The meal prep page will show \"the next menu is in the works\" until you publish a new one.")) {
                          archiveMenu(m.id);
                        }
                      }}
                      data-testid={`take-down-menu-button-${m.id}`}
                      className="cursor-pointer rounded-full border border-terracotta/40 px-3 py-1 text-xs text-terracotta hover:bg-terracotta hover:text-cream"
                    >
                      Take down
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm("Delete this live menu permanently? The meal prep page will show \"the next menu is in the works\" until you publish a new one. Orders already placed are kept.")) {
                          deleteMenu(m.id);
                        }
                      }}
                      data-testid={`menu-delete-${m.id}`}
                      className="cursor-pointer rounded-full border border-terracotta/40 px-3 py-1 text-xs text-terracotta hover:bg-terracotta hover:text-cream"
                    >
                      Delete
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-[11px] uppercase tracking-[0.14em] text-ink/40">
                      {m.show_in_archive === false ? "Hidden from site" : "On public archive"}
                    </span>
                    <button
                      onClick={() => setMenuVisibility(m.id, m.show_in_archive === false)}
                      data-testid={`menu-visibility-${m.id}`}
                      className="cursor-pointer rounded-full border border-forest/20 px-3 py-1 text-xs text-forest/70 hover:bg-forest hover:text-cream"
                    >
                      {m.show_in_archive === false ? "Show on site" : "Hide from site"}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm("Delete this menu permanently? This can't be undone. Orders already placed are kept.")) {
                          deleteMenu(m.id);
                        }
                      }}
                      data-testid={`menu-delete-${m.id}`}
                      className="cursor-pointer rounded-full border border-terracotta/40 px-3 py-1 text-xs text-terracotta hover:bg-terracotta hover:text-cream"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ------- Meal prep orders ------- */}
      <div className="mt-10 rounded-3xl border border-forest/15 bg-white/50 p-6 md:p-10" data-testid="admin-orders">
        <h2 className="font-display text-3xl font-light text-forest" data-testid="admin-orders-heading">Orders</h2>
        <div className="mt-6 space-y-4" data-testid="admin-orders-list">
          {orders.length === 0 && <p className="text-sm text-ink/60">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid="admin-order-card" className="rounded-2xl border border-forest/10 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-display text-lg text-forest">
                  {o.name} — {o.kind === "holiday" ? <span className="text-terracotta">Holiday trays</span> : o.week_label}
                </p>
                <div className="flex items-center gap-2">
                  {o.kind === "holiday" && (
                    <span className="rounded-full bg-terracotta/10 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-terracotta" data-testid="admin-order-holiday-badge">
                      Holiday
                    </span>
                  )}
                  <span className="rounded-full bg-gold/25 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-forest">{(o.status || "new").replace("_", " ")}</span>
                </div>
              </div>
              {o.kind === "holiday" ? (
                <>
                  <p className="mt-1 text-sm text-ink/70">
                    {o.items.map((it) => `${it.item} — ${it.size === "half" ? "Half" : "Full"} tray ×${it.qty}`).join(" · ")}
                    {" — "}<strong>{money(o.subtotal_cents)}</strong>
                  </p>
                  <p className="mt-1 text-sm text-ink/60">
                    {o.fulfillment === "delivery"
                      ? `Delivery · ${o.delivery_date} · ${o.delivery_address}`
                      : `Pickup · ${o.pickup_date}${o.pickup_time ? ` · ${o.pickup_time}` : ""}`}
                  </p>
                  {o.notes && <p className="mt-2 text-sm italic text-ink/70">"{o.notes}"</p>}
                </>
              ) : (
                <>
                  <p className="mt-1 text-sm text-ink/70">
                    {o.order_type === "a_la_carte"
                      ? `À la carte · ${o.allocations?.reduce((a, b) => a + (b.qty || 0), 0) || 0} meals`
                      : o.items.map((it) => `${it.name} ×${it.qty}`).join(" · ")}
                    {" — "}<strong>{money(o.total_cents)}</strong>
                  </p>
                  {o.allocations?.length > 0 && (
                    <p className="mt-1 text-sm text-terracotta" data-testid="admin-order-mix">
                      Mix: {o.allocations.map((a) => `${a.name} ×${a.qty}`).join(" · ")}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-ink/60">{o.email}{o.phone ? ` · ${o.phone}` : ""}{o.delivery_date ? ` · ${o.delivery_date}` : ""}</p>
                  {o.delivery_notes && <p className="mt-2 text-sm italic text-ink/70">"{o.delivery_notes}"</p>}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ------- Guest words ------- */}
      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div className="rounded-3xl border border-forest/15 bg-white/50 p-6 md:p-10" data-testid="admin-guest-words">
          <h2 className="font-display text-3xl font-light text-forest">Guest words</h2>
          <p className="mt-2 text-sm text-ink/60">Add a guest's words and it appears on the Home page right away.</p>
          <form onSubmit={addWord} className="mt-6 space-y-4">
            <textarea
              required
              rows="3"
              value={tw.quote}
              onChange={(e) => setTw((f) => ({ ...f, quote: e.target.value }))}
              placeholder="The guest's words…"
              data-testid="testimonial-quote-textarea"
              className="field resize-y"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <input required value={tw.guest} onChange={(e) => setTw((f) => ({ ...f, guest: e.target.value }))} placeholder="Guest name" data-testid="testimonial-guest-input" className="field" />
              <input value={tw.context} onChange={(e) => setTw((f) => ({ ...f, context: e.target.value }))} placeholder="Context (e.g. September supper club)" data-testid="testimonial-context-input" className="field" />
            </div>
            <div className="flex items-center gap-4">
              <button type="submit" data-testid="add-testimonial-button" className="cursor-pointer rounded-full bg-terracotta px-7 py-3 text-sm font-medium text-cream transition-colors hover:bg-gold hover:text-deepforest">
                Add guest words
              </button>
              {wordMsg && <p data-testid="testimonial-add-status" className="text-sm text-terracotta">{wordMsg}</p>}
            </div>
          </form>
        </div>
        <div className="rounded-3xl border border-forest/15 bg-white/50 p-6 md:p-10">
          <div className="space-y-4" data-testid="admin-testimonials-list">
            {testimonials.length === 0 && <p className="text-sm text-ink/60">Nothing yet — the Home section stays tucked away until the first words land.</p>}
            {testimonials.map((t) => (
              <div key={t.id} data-testid="admin-testimonial-row" className="flex items-start justify-between gap-4 rounded-2xl border border-forest/10 p-4">
                <div>
                  <p className="text-sm italic text-ink/85">"{t.quote}"</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.14em] text-ink/50">{t.guest}{t.context ? ` · ${t.context}` : ""}</p>
                </div>
                <button
                  onClick={() => removeWord(t.id)}
                  aria-label="Remove guest words"
                  data-testid={`remove-testimonial-${t.id}`}
                  className="cursor-pointer rounded-full p-2 text-terracotta/70 transition-colors hover:bg-terracotta/10 hover:text-terracotta"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ------- Inquiries + subscribers ------- */}
      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3">
            <Mail size={18} className="text-terracotta" />
            <h2 className="font-display text-2xl text-forest">Inquiries</h2>
            <span className="text-sm text-ink/50">{inquiries.length}</span>
          </div>
          <div className="mt-5 space-y-4" data-testid="admin-inquiries-list">
            {inquiries.length === 0 && <p className="text-sm text-ink/60">No inquiries yet.</p>}
            {inquiries.map((inq) => (
              <div key={inq.id} data-testid="admin-inquiry-card" className="rounded-2xl border border-forest/10 bg-white/50 p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display text-lg text-forest">{inq.name}</p>
                  <span className="rounded-full bg-gold/20 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-forest">{inq.interest}</span>
                </div>
                <p className="mt-1 text-sm text-ink/70">
                  {inq.email}{inq.phone ? ` · ${inq.phone}` : ""}{inq.guests ? ` · ${inq.guests} guests` : ""}{inq.event_date ? ` · ${inq.event_date}` : ""}
                </p>
                {inq.message && <p className="mt-3 text-sm leading-relaxed text-ink/80">{inq.message}</p>}
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-3">
            <Users size={18} className="text-terracotta" />
            <h2 className="font-display text-2xl text-forest">Email list</h2>
            <span className="text-sm text-ink/50">{subscribers.length}</span>
          </div>
          <div className="mt-5 rounded-2xl border border-forest/10 bg-white/50 p-6" data-testid="admin-subscribers-list">
            {subscribers.length === 0 && <p className="text-sm text-ink/60">No subscribers yet.</p>}
            {subscribers.map((s) => (
              <p key={s.id} data-testid="admin-subscriber-row" className="border-b border-forest/5 py-2 text-sm text-ink/80 last:border-0">
                {s.email}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SignInPanel() {
  return (
    <div data-testid="admin-signin-panel" className="flex min-h-[70vh] items-center justify-center px-6">
      <div className="w-full max-w-md rounded-3xl border border-forest/15 bg-white/50 px-8 py-14 text-center">
        <img src={ASSETS.sitaLogo} alt="Sita" className="mx-auto h-20 w-20" />
        <h1 className="mt-8 font-display text-3xl font-light text-forest">Owner sign-in</h1>
        <p className="mt-3 text-sm leading-relaxed text-ink/70">
          This table is reserved for the owner — sign in to see inquiries, orders, and guest words.
        </p>
        <button
          onClick={googleAuthRedirect}
          data-testid="google-signin-button"
          className="mt-8 inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-3.5 text-sm font-medium tracking-wide text-cream transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-deepforest"
        >
          <LogIn size={16} /> Continue with Google
        </button>
        <p className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-ink/40">
          <Sprout size={12} /> Emergent-managed Google auth
        </p>
      </div>
    </div>
  );
}

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(location.state?.user ? false : null);

  if (location.hash?.includes("session_id=")) {
    return (
      <AuthCallback
        onDone={async () => {
          setChecking(null);
          try {
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(data);
          } catch (e) {
            setUser(false);
          }
          setChecking(false);
        }}
      />
    );
  }

  if (checking === null && user === null) {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }

  return (
    <div>
      <Seo title="Owner Dashboard — Sita" description="Private dashboard for Sita inquiries, orders, and guest words." />
      {checking === null && user === null ? (
        <div className="flex min-h-[70vh] items-center justify-center">
          <p className="font-display text-2xl italic text-forest">Setting the table…</p>
        </div>
      ) : user && user.email ? (
        <Dashboard user={user} />
      ) : (
        <SignInPanel />
      )}
    </div>
  );
}
